import { useState, useMemo } from 'react'
import { X, Plus, ChevronRight, ChevronDown, TriangleAlert } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { CurrencyInput } from '@/components/ui/currency-input'
import { Textarea } from '@/components/ui/textarea'
import { SuggestionInput } from '@/components/ui/suggestion-input'
import { Label } from '@/components/ui/label'
import { CategorySelect } from '@/components/category/category-select'
import { TagSelect } from '@/components/tag/tag-select'
import type { Profile, Category, Tag } from '@/lib/hooks'
import { formatAmount } from '@/lib/utils'

export type ItemRow = {
  id: string
  name: string
  amount: number | ''
  category_id: string | null
}

export function createEmptyItem(): ItemRow {
  return { id: crypto.randomUUID(), name: '', amount: '', category_id: null }
}

type TransactionFormProps = {
  date: string
  storeName: string
  memo: string
  items: ItemRow[]
  paidBy: string
  profiles: Profile[]
  categories: Category[]
  scope?: 'household' | 'personal'
  taxAmount?: number
  tags?: Tag[]
  selectedTagIds?: string[]
  lastUsedTags?: Tag[]
  storeNameSuggestions?: string[]
  itemNameSuggestions?: string[]
  onDateChange: (date: string) => void
  onStoreNameChange: (name: string) => void
  onMemoChange: (memo: string) => void
  onItemsChange: (items: ItemRow[]) => void
  onPaidByChange: (userId: string) => void
  onTaxAmountChange?: (amount: number) => void
  onScopeChange?: (scope: 'household' | 'personal') => void
  onTagIdsChange?: (ids: string[]) => void
  onCreateTag?: (data: { name: string; color: string }) => void
}

export function TransactionForm({
  date,
  storeName,
  memo,
  items,
  paidBy,
  profiles,
  categories,
  scope = 'household',
  taxAmount,
  tags,
  selectedTagIds,
  lastUsedTags,
  storeNameSuggestions,
  itemNameSuggestions,
  onDateChange,
  onStoreNameChange,
  onMemoChange,
  onItemsChange,
  onPaidByChange,
  onTaxAmountChange,
  onScopeChange,
  onTagIdsChange,
  onCreateTag,
}: TransactionFormProps) {
  const totalAmount = useMemo(
    () => items.reduce((sum, item) => sum + (Number(item.amount) || 0), 0) + (taxAmount ?? 0),
    [items, taxAmount]
  )

  const [bulkOpen, setBulkOpen] = useState(false)

  const bulkDefaultCategory = useMemo(() => {
    if (items.length < 2) return null
    const first = items[0].category_id
    if (first && items.every((item) => item.category_id === first)) return first
    return null
  }, [items])

  const categoryMap = useMemo(
    () => new Map(categories.map((c) => [c.id, c])),
    [categories]
  )

  const hasPersonalCategory = useMemo(
    () => items.some((item) => {
      const cat = item.category_id ? categoryMap.get(item.category_id) : null
      return cat?.owner_id != null
    }),
    [items, categoryMap]
  )

  const switchToPersonalIfNeeded = () => {
    if (scope !== 'personal' && onScopeChange) {
      onScopeChange('personal')
    }
  }

  const applyBulkCategory = (categoryId: string | null) => {
    onItemsChange(items.map((item) => ({ ...item, category_id: categoryId })))
    setBulkOpen(false)
  }

  const updateItem = (index: number, field: keyof ItemRow, value: string | number | null) => {
    const next = [...items]
    next[index] = { ...next[index], [field]: value }
    onItemsChange(next)
  }

  const addItem = () => {
    onItemsChange([...items, createEmptyItem()])
  }

  const removeItem = (index: number) => {
    if (items.length <= 1) return
    onItemsChange(items.filter((_, i) => i !== index))
  }

  const dateHint = useMemo(() => {
    if (!date) return null
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const selected = new Date(date + 'T00:00:00')
    if (isNaN(selected.getTime())) return null
    const diffMs = selected.getTime() - today.getTime()
    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24))
    if (diffDays === 0) return null
    const absDays = Math.abs(diffDays)
    const rtf = new Intl.RelativeTimeFormat('ja', { numeric: 'auto' })
    let relative: string
    if (absDays < 7) {
      relative = rtf.format(diffDays, 'day')
    } else if (absDays < 28) {
      relative = rtf.format(Math.round(diffDays / 7), 'week')
    } else {
      relative = rtf.format(Math.round(diffDays / 30), 'month')
    }
    return `${relative}の日付です`
  }, [date])

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>日付</Label>
        <Input
          type="date"
          value={date}
          onChange={(e) => onDateChange(e.target.value)}
        />
        {dateHint && (
          <div className="flex items-center gap-2 rounded-md border border-amber-400/50 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800 dark:border-amber-500/40 dark:bg-amber-950/50 dark:text-amber-300">
            <TriangleAlert className="size-4 shrink-0" />
            <span>{dateHint}</span>
          </div>
        )}
      </div>

      <div className="space-y-2">
        <Label>店名（任意）</Label>
        <SuggestionInput
          type="text"
          value={storeName}
          onValueChange={onStoreNameChange}
          suggestions={storeNameSuggestions}
          autoCapitalize="off"
          enterKeyHint="next"
          placeholder="店名"
        />
      </div>

      <div className="space-y-2">
        <Label>メモ（任意）</Label>
        <Textarea
          rows={2}
          value={memo}
          onChange={(e) => onMemoChange(e.target.value)}
          placeholder="メモ"
        />
      </div>

      {tags && selectedTagIds && onTagIdsChange && (
        <TagSelect
          tags={tags}
          selectedTagIds={selectedTagIds}
          onTagIdsChange={onTagIdsChange}
          onCreateTag={onCreateTag}
          lastUsedTags={lastUsedTags}
        />
      )}

      <div className="space-y-3">
        <Label>品目明細</Label>
        {items.length > 1 && (
          <div className="rounded-lg border">
            <button
              type="button"
              onClick={() => setBulkOpen((v) => !v)}
              className="flex w-full items-center gap-3 px-3 py-2.5"
            >
              <span className="flex-1 text-left text-xs font-medium text-muted-foreground">
                カテゴリ一括変更
              </span>
              {bulkOpen ? (
                <ChevronDown className="size-3.5 text-muted-foreground" />
              ) : (
                <ChevronRight className="size-3.5 text-muted-foreground" />
              )}
            </button>
            {bulkOpen && (
              <div className="border-t px-3 py-2.5">
                <CategorySelect
                  categories={categories}
                  value={bulkDefaultCategory}
                  onChange={applyBulkCategory}
                  showUncategorized={false}
                  onPersonalCategorySelected={switchToPersonalIfNeeded}
                />
              </div>
            )}
          </div>
        )}
        {items.map((item, index) => (
            <div key={item.id} className="space-y-2 rounded-lg border p-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-muted-foreground">
                  {`品目 ${index + 1}`}
                </span>
                {items.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeItem(index)}
                    aria-label={`品目 ${index + 1} を削除`}
                  >
                    <X className="size-4" />
                  </Button>
                )}
              </div>
              <SuggestionInput
                type="text"
                value={item.name}
                onValueChange={(v) => updateItem(index, 'name', v)}
                suggestions={itemNameSuggestions}
                placeholder="品名"
                autoCapitalize="off"
                enterKeyHint="next"
              />
              <CurrencyInput
                value={item.amount}
                onChange={(e) =>
                  updateItem(index, 'amount', e.target.value === '' ? '' : Number(e.target.value))
                }
                placeholder="金額"
              />
              <CategorySelect
                categories={categories}
                value={item.category_id}
                onChange={(v) => updateItem(index, 'category_id', v)}
                onPersonalCategorySelected={switchToPersonalIfNeeded}
              />
            </div>
        ))}
        <Button type="button" variant="outline" className="w-full" onClick={addItem}>
          <Plus className="size-4" />
          品目を追加
        </Button>
      </div>

      {onTaxAmountChange && (
        <div className="space-y-2">
          <Label>外税（任意）</Label>
          <CurrencyInput
            value={taxAmount || ''}
            onChange={(e) =>
              onTaxAmountChange(e.target.value === '' ? 0 : Number(e.target.value))
            }
            placeholder="外税額"
          />
        </div>
      )}

      <div className="rounded-lg bg-muted p-3 text-center">
        <span className="text-sm text-muted-foreground">合計金額</span>
        <p className="text-2xl font-bold">
          {formatAmount(totalAmount)}
        </p>
        {(taxAmount ?? 0) > 0 && (
          <p className="mt-1 text-xs text-muted-foreground">
            (うち外税 {formatAmount(taxAmount!)})
          </p>
        )}
      </div>

      {onScopeChange && (
        <div className="space-y-2">
          <Label>区分</Label>
          <div className="flex gap-2">
            <Button
              type="button"
              variant={scope === 'household' ? 'default' : 'outline'}
              className="flex-1"
              onClick={() => onScopeChange('household')}
              disabled={hasPersonalCategory}
            >
              家計
            </Button>
            <Button
              type="button"
              variant={scope === 'personal' ? 'default' : 'outline'}
              className="flex-1"
              onClick={() => onScopeChange('personal')}
            >
              個人
            </Button>
          </div>
          {hasPersonalCategory && (
            <p className="text-xs text-muted-foreground">
              ↑ 個人カテゴリが含まれているため「家計」に変更できません
            </p>
          )}
        </div>
      )}

      {scope !== 'personal' && (
        <div className="space-y-2">
          <Label>支払者</Label>
          <div className="flex gap-2">
            {profiles.map((profile) => (
              <Button
                key={profile.id}
                type="button"
                variant={paidBy === profile.id ? 'default' : 'outline'}
                className="flex-1"
                onClick={() => onPaidByChange(profile.id)}
              >
                {profile.display_name}
              </Button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
